import type { ReactNode } from "react"

import {
  Disclosure,
  Panel,
  Section,
  SectionHeading,
} from "../BatteryOnlyProposal/primitives"
import { useDesktopViewport } from "../BatteryOnlyProposal/use-desktop-viewport"

export type NextStep = {
  day: string
  title: string
  body: string
}

const stepNumberClass = (index: number): string =>
  index === 0
    ? `bg-bp-accent text-bp-on-accent`
    : `border border-bp-border-strong bg-bp-elevated-2 text-bp-text-muted`

/**
 * "What happens next", shared by every proposal kind. Each kind passes its own
 * heading and steps. Desktop shows a numbered grid; below `lg` the steps
 * collapse into disclosures with the first one open.
 */
export function NextSteps({
  heading,
  steps,
}: {
  /** The line after the "What happens next" eyebrow, including its `GradText`. */
  heading: ReactNode
  /**
   * The timeline in order. The desktop grid is laid out for five steps
   * (`xl:grid-cols-5`); a different count wraps or leaves empty columns at
   * `xl`, so change the grid with the copy.
   */
  steps: readonly NextStep[]
}): JSX.Element {
  const isDesktop = useDesktopViewport()

  if (!isDesktop) {
    return (
      <Section id="next">
        <SectionHeading eyebrow="What happens next">{heading}</SectionHeading>
        <div className="flex flex-col gap-2">
          {steps.map((step, index) => (
            <Disclosure
              key={step.title}
              defaultOpen={index === 0}
              summary={
                <span className="flex flex-1 items-center justify-between gap-3">
                  <span className="flex items-center gap-3">
                    <span
                      className={`font-display grid size-7 place-items-center rounded-full text-[length:var(--step--2)] font-bold ${stepNumberClass(index)}`}
                    >
                      {index + 1}
                    </span>
                    <span className="font-display text-[length:var(--step--1)] font-semibold text-bp-text">
                      {step.title}
                    </span>
                  </span>
                  <span className="text-[length:var(--step--3)] font-semibold tracking-widest text-bp-text-muted uppercase">
                    {step.day}
                  </span>
                </span>
              }
            >
              <p className="text-[length:var(--step--1)] leading-snug text-bp-text-muted">
                {step.body}
              </p>
            </Disclosure>
          ))}
        </div>
      </Section>
    )
  }

  return (
    <Section id="next">
      <SectionHeading eyebrow="What happens next">{heading}</SectionHeading>
      <Panel className="p-8">
        <div className="relative grid gap-6 sm:grid-cols-2 xl:grid-cols-5">
          {steps.map((step, index) => (
            <div key={step.title} className="relative">
              <div
                className={`font-display mb-3.5 grid size-11 place-items-center rounded-full text-[length:var(--step-0)] font-bold ${stepNumberClass(index)}`}
              >
                {index + 1}
              </div>
              <div className="mb-1 text-[length:var(--step--2)] font-semibold tracking-widest text-bp-text-muted uppercase">
                {step.day}
              </div>
              <h3 className="mb-1.5 text-[length:var(--step-0)] font-semibold tracking-[-0.01em] text-bp-text">
                {step.title}
              </h3>
              <p className="text-[length:var(--step--1)] leading-snug text-bp-text-muted">
                {step.body}
              </p>
            </div>
          ))}
        </div>
      </Panel>
    </Section>
  )
}
